// Shared spotlight-overlay engine used by both the vendor TourProvider and the
// main-app MiniTourProvider. Neither provider's behavior or persistence logic
// changes — only the DOM-rendering layer is shared here.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// ─── Shared types ────────────────────────────────────────────────────────────

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStep {
  target: string;
  title: string;
  body: string;
  videoUrl?: string;
  placement?: TourPlacement;
  /**
   * Optional CSS selector for an element to click before measuring `target`.
   * Use this when `target` is hidden behind a tab/accordion that must be opened
   * for the spotlight to land on a visible element. The click is fired only if
   * the element exists; missing elements are silently ignored so a stale
   * selector doesn't break the rest of the tour.
   */
  activate?: string;
}

export interface TourConfig {
  id: string;
  steps: TourStep[];
}

// ─── SpotlightOverlay ────────────────────────────────────────────────────────

interface TargetRect {
  top: number; left: number; width: number; height: number;
}

export interface SpotlightOverlayProps {
  tour: TourConfig;
  stepIndex: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onClose: () => void;
  /** data-testid prefix, e.g. "tour" → "tour-overlay", "guide" → "guide-overlay" */
  testIdPrefix?: string;
  /** Tailwind class(es) for the primary action button */
  actionButtonClass?: string;
  /** Label for the skip link */
  skipLabel?: string;
}

export function SpotlightOverlay({
  tour,
  stepIndex,
  onNext,
  onPrev,
  onSkip,
  onClose,
  testIdPrefix = "tour",
  actionButtonClass = "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  skipLabel = "Skip",
}: SpotlightOverlayProps) {
  const step = tour.steps[stepIndex];
  const totalSteps = tour.steps.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const [rect, setRect] = useState<TargetRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipSize, setTooltipSize] = useState<{ width: number; height: number }>({ width: 360, height: 200 });
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let hasScrolled = false;
    // MutationObserver and its fallback timer used when an `activate` step
    // lazily mounts its target panel. Both are cleaned up on effect teardown
    // (e.g. the user clicks Next before the panel finishes mounting) so they
    // never fire against a stale step.
    let activateMo: MutationObserver | null = null;
    let activateFallback: ReturnType<typeof setTimeout> | null = null;

    // Reset rect when the step changes so a missing target on the new step
    // doesn't keep showing the previous step's spotlight position.
    setRect(null);

    // If the step declares an `activate` selector (e.g. a tab trigger that
    // must be clicked to reveal the target), click it now before we measure.
    // Silently ignore a missing element — a stale selector should not break
    // the rest of the tour.
    if (step.activate) {
      try {
        const trigger = document.querySelector(step.activate) as HTMLElement | null;
        if (trigger) {
          // A controlled Radix <Tabs> trigger switches the active tab on
          // mousedown / focus / keydown — it has NO onClick handler — so a bare
          // trigger.click() (which dispatches only a `click` event, fires no
          // mousedown and moves no focus) silently fails to switch the tab. The
          // step's title/counter still advance, but the panel never mounts and
          // the spotlight ring lands nowhere. Dispatch a faithful pointer
          // sequence plus focus so BOTH Radix triggers AND plain onClick targets
          // (inbox rows, buttons, hand-rolled tab buttons) activate.
          try { trigger.focus({ preventScroll: true }); } catch { /* ignore */ }
          const mouseInit: MouseEventInit = { bubbles: true, cancelable: true, button: 0 };
          trigger.dispatchEvent(new MouseEvent("mousedown", mouseInit));
          trigger.dispatchEvent(new MouseEvent("mouseup", mouseInit));
          trigger.click();
        }
      } catch { /* ignore */ }
    }

    function measure() {
      const el = document.querySelector(step.target) as HTMLElement | null;
      if (!el) {
        // Target missing — keep retrying for a short window in case the
        // element is still mounting (e.g. data-dependent UI). DO NOT
        // auto-advance; the tooltip will render centered on screen so the
        // user can still read the step and click Next themselves. Auto-
        // advancing here was the source of the "skipping 2 steps at a time"
        // bug: a missing step-N target silently jumped to step N+1, and a
        // missing last-step target silently closed the tour.
        attempts += 1;
        if (attempts < 12 && !cancelled) {
          setTimeout(measure, 150);
        }
        return;
      }
      // Scroll the target on-screen the FIRST time it's actually present, not
      // before. When a step uses `activate` to click into a tab/accordion, the
      // revealed panel — and the target inside it — only mounts AFTER the click,
      // so scrolling up-front (against a not-yet-rendered target) silently no-ops
      // and the spotlight ring lands off the viewport. Deferring the scroll into
      // measure() means we re-measure once the panel has rendered and then scroll
      // it into view. The scroll guard (hasScrolled) keeps the smooth-scroll's own
      // scroll events — which re-fire measure() — from re-triggering scrollIntoView.
      if (!hasScrolled) {
        hasScrolled = true;
        try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }); } catch { /* ignore */ }
      }
      const r = el.getBoundingClientRect();
      if (!cancelled) {
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    }

    if (step.activate) {
      // Deterministic activate-then-wait: for a controlled Radix <Tabs> the
      // tab content only mounts after React flushes the onValueChange state
      // update — a fixed blind-wait of 150 ms is not reliable across network
      // speeds and CPU loads. Instead we take two branches:
      //
      //  A. Target already in DOM (tab was visited before, Radix keeps it
      //     mounted): give React one short settle window so the active-tab
      //     re-render propagates before we measure.
      //
      //  B. Target NOT yet in DOM (lazy first mount): watch for React to
      //     insert the panel with a MutationObserver so we fire measure()
      //     the moment the element appears, then fall back to the standard
      //     retry-poll if the observer hasn't fired within 1.5 s.
      if (document.querySelector(step.target)) {
        // Branch A — already mounted, just settle.
        setTimeout(measure, 100);
      } else {
        // Branch B — lazy mount; observe the DOM for the target's arrival.
        let moFired = false;
        activateMo = new MutationObserver(() => {
          if (!cancelled && document.querySelector(step.target)) {
            moFired = true;
            activateMo!.disconnect();
            activateMo = null;
            if (activateFallback !== null) {
              clearTimeout(activateFallback);
              activateFallback = null;
            }
            measure();
          }
        });
        activateMo.observe(document.body, { childList: true, subtree: true });
        // Fallback: if the observer hasn't fired in 1.5 s, drop into the
        // standard polling loop (measure() retries up to 12 × 150 ms).
        activateFallback = setTimeout(() => {
          activateFallback = null;
          if (!moFired && !cancelled) {
            activateMo?.disconnect();
            activateMo = null;
            measure();
          }
        }, 1500);
      }
    } else {
      // Non-activate step: target is already present in the DOM. Wait a short
      // settle period so any entrance animation completes before measuring.
      setTimeout(measure, 350);
    }

    const onResize = () => measure();
    const onScroll = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    const mql = window.matchMedia("(max-width: 639px)");
    const onMqChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onMqChange);
    return () => {
      cancelled = true;
      // Clean up any in-flight activate observer/timer so they don't fire
      // against a step that's already been replaced.
      activateMo?.disconnect();
      if (activateFallback !== null) clearTimeout(activateFallback);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      mql.removeEventListener("change", onMqChange);
    };
  }, [step.target, tour.id, stepIndex]);

  useEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const observer = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setTooltipSize({ width: r.width, height: r.height });
    });
    observer.observe(el);
    const r = el.getBoundingClientRect();
    setTooltipSize({ width: r.width, height: r.height });
    return () => observer.disconnect();
  }, [stepIndex, rect]);

  const placement = useMemo<TourPlacement>(() => {
    if (!rect) return step.placement ?? "bottom";
    if (step.placement && step.placement !== "auto") return step.placement;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const candidates: Array<[TourPlacement, number]> = [
      ["bottom", vh - (rect.top + rect.height)],
      ["top", rect.top],
      ["right", vw - (rect.left + rect.width)],
      ["left", rect.left],
    ];
    candidates.sort((a, b) => b[1] - a[1]);
    return candidates[0][0];
  }, [rect, step.placement]);

  const SPOT_PAD = 8;
  const spotRect = rect ? {
    top: Math.max(0, rect.top - SPOT_PAD),
    left: Math.max(0, rect.left - SPOT_PAD),
    width: rect.width + SPOT_PAD * 2,
    height: rect.height + SPOT_PAD * 2,
  } : null;

  const TOOLTIP_GAP = 12;
  const ARROW = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const tw = tooltipSize.width || 360;
  const th = tooltipSize.height || 200;

  // Default to a centered tooltip with no arrow. Used when the target is
  // missing (or hasn't measured yet) so the user can still read the step
  // and click Next/Skip themselves instead of the tour silently skipping.
  let tooltipStyle: React.CSSProperties = {
    top: Math.max(8, vh / 2 - th / 2),
    left: Math.max(8, vw / 2 - tw / 2),
    width: tw,
    visibility: "visible",
  };
  let arrowStyle: React.CSSProperties = { display: "none" };
  if (rect && spotRect) {
    let top = 0;
    let left = 0;
    if (placement === "bottom") {
      top = spotRect.top + spotRect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
    } else if (placement === "top") {
      top = spotRect.top - th - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
    } else if (placement === "right") {
      top = rect.top + rect.height / 2 - th / 2;
      left = spotRect.left + spotRect.width + TOOLTIP_GAP;
    } else if (placement === "left") {
      top = rect.top + rect.height / 2 - th / 2;
      left = spotRect.left - tw - TOOLTIP_GAP;
    }
    const GUT = 8;
    left = Math.max(GUT, Math.min(left, vw - tw - GUT));
    top = Math.max(GUT, Math.min(top, vh - th - GUT));
    tooltipStyle = { top, left, width: tw, visibility: "visible" };

    const targetCenterX = rect.left + rect.width / 2;
    const targetCenterY = rect.top + rect.height / 2;
    if (placement === "bottom") {
      arrowStyle = { top: -ARROW, left: Math.max(12, Math.min(tw - 20, targetCenterX - left - ARROW)), borderLeft: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid white` };
    } else if (placement === "top") {
      arrowStyle = { bottom: -ARROW, left: Math.max(12, Math.min(tw - 20, targetCenterX - left - ARROW)), borderLeft: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid transparent`, borderTop: `${ARROW}px solid white` };
    } else if (placement === "right") {
      arrowStyle = { left: -ARROW, top: Math.max(12, Math.min(th - 20, targetCenterY - top - ARROW)), borderTop: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid white` };
    } else if (placement === "left") {
      arrowStyle = { right: -ARROW, top: Math.max(12, Math.min(th - 20, targetCenterY - top - ARROW)), borderTop: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid transparent`, borderLeft: `${ARROW}px solid white` };
    }
  }

  const card = (
    <SpotlightTooltipCard
      step={step}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      isFirst={isFirst}
      isLast={isLast}
      onNext={onNext}
      onPrev={onPrev}
      onSkip={onSkip}
      onClose={onClose}
      testIdPrefix={testIdPrefix}
      actionButtonClass={actionButtonClass}
      skipLabel={skipLabel}
    />
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      data-testid={`${testIdPrefix}-overlay`}
      onClick={(e) => { e.stopPropagation(); }}
    >
      {spotRect ? (
        <>
          <div className="absolute bg-black/60 transition-opacity duration-200" style={{ top: 0, left: 0, right: 0, height: spotRect.top }} />
          <div className="absolute bg-black/60 transition-opacity duration-200" style={{ top: spotRect.top + spotRect.height, left: 0, right: 0, bottom: 0 }} />
          <div className="absolute bg-black/60 transition-opacity duration-200" style={{ top: spotRect.top, left: 0, width: spotRect.left, height: spotRect.height }} />
          <div className="absolute bg-black/60 transition-opacity duration-200" style={{ top: spotRect.top, left: spotRect.left + spotRect.width, right: 0, height: spotRect.height }} />
          <div className="absolute rounded-lg pointer-events-none ring-2 ring-white/80 shadow-[0_0_0_4px_rgba(99,149,255,0.35)]" style={{ top: spotRect.top, left: spotRect.left, width: spotRect.width, height: spotRect.height }} />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/60" />
      )}

      {isMobile ? (
        <SpotlightTooltipCard
          step={step}
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          isFirst={isFirst}
          isLast={isLast}
          onNext={onNext}
          onPrev={onPrev}
          onSkip={onSkip}
          onClose={onClose}
          testIdPrefix={testIdPrefix}
          actionButtonClass={actionButtonClass}
          skipLabel={skipLabel}
          mobileSheet
        />
      ) : (
        <div
          ref={tooltipRef}
          className="absolute"
          style={tooltipStyle}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-tooltip`}
        >
          <span className="absolute w-0 h-0" style={arrowStyle} aria-hidden="true" />
          {card}
        </div>
      )}
    </div>,
    document.body,
  );
}

// ─── SpotlightRing ───────────────────────────────────────────────────────────
// Lightweight reusable highlight ring — the same visual treatment the full
// SpotlightOverlay uses on its current step target, but without the dark
// backdrop or tooltip. Portal'd to document.body so it floats above the page.
// Used by GuideNudgeBanner to highlight the Guide / Explainer Video buttons.

interface SpotlightRingProps {
  /** CSS selector for the element to highlight (e.g. `[data-testid="button-guide-foo"]`). */
  selector: string;
  /**
   * Optional group tag stamped onto the portal node as `data-spotlight-ring-group`.
   * Lets an owner (e.g. GuideNudgeBanner) imperatively sweep any orphaned ring
   * portals belonging to it that React failed to unmount during navigation.
   */
  groupKey?: string;
}

export function SpotlightRing({ selector, groupKey }: SpotlightRingProps) {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    let cancelled = false;

    function measure() {
      try {
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) { if (!cancelled) setRect(null); return; }
        const r = el.getBoundingClientRect();
        if (!cancelled) setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } catch { /* ignore bad selectors */ }
    }

    measure();
    const onResize = () => measure();
    const onScroll = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    let ro: ResizeObserver | null = null;
    try {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        ro = new ResizeObserver(() => measure());
        ro.observe(el);
      }
    } catch { /* ignore */ }

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      ro?.disconnect();
    };
  }, [selector]);

  if (!rect) return null;

  const SPOT_PAD = 6;
  const spotRect = {
    top: Math.max(0, rect.top - SPOT_PAD),
    left: Math.max(0, rect.left - SPOT_PAD),
    width: rect.width + SPOT_PAD * 2,
    height: rect.height + SPOT_PAD * 2,
  };

  return createPortal(
    <div
      className="fixed pointer-events-none z-[9998] rounded-lg ring-2 ring-white/80 shadow-[0_0_0_4px_rgba(99,149,255,0.35)]"
      style={{ top: spotRect.top, left: spotRect.left, width: spotRect.width, height: spotRect.height }}
      aria-hidden="true"
      data-spotlight-ring-group={groupKey}
      data-testid={`spotlight-ring-${selector.replace(/\W+/g, "-")}`}
    />,
    document.body,
  );
}

// ─── SpotlightTooltipCard ────────────────────────────────────────────────────

interface SpotlightTooltipCardProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onClose: () => void;
  testIdPrefix: string;
  actionButtonClass: string;
  skipLabel: string;
  mobileSheet?: boolean;
}

function SpotlightTooltipCard({
  step, stepIndex, totalSteps, isFirst, isLast,
  onNext, onPrev, onSkip, onClose,
  testIdPrefix, actionButtonClass, skipLabel,
  mobileSheet,
}: SpotlightTooltipCardProps) {
  const wrapperClass = mobileSheet
    ? "fixed left-0 right-0 bottom-0 bg-white text-gray-900 rounded-t-xl shadow-2xl border-t border-gray-200 z-[10000]"
    : "bg-white text-gray-900 rounded-lg shadow-xl border border-gray-200 w-[360px] max-w-[calc(100vw-16px)]";
  return (
    <div className={wrapperClass} onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-tooltip-card`}>
      <div className="p-4 relative">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 rounded-md p-1"
          data-testid={`button-${testIdPrefix}-close`}
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-semibold text-base text-gray-900 pr-6" data-testid={`text-${testIdPrefix}-title`}>{step.title}</h3>
        <p className="text-xs text-gray-500 mt-1" data-testid={`text-${testIdPrefix}-step-counter`}>Step {stepIndex + 1} of {totalSteps}</p>
        <p className="text-sm text-gray-700 mt-3 leading-relaxed" data-testid={`text-${testIdPrefix}-body`}>{step.body}</p>
        {step.videoUrl && (
          <div className="mt-3 aspect-video w-full overflow-hidden rounded-md bg-black">
            <iframe
              className="w-full h-full"
              src={toEmbedUrl(step.videoUrl)}
              title={step.title}
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid={`iframe-${testIdPrefix}-video`}
            />
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
            data-testid={`button-${testIdPrefix}-skip`}
          >
            {skipLabel}
          </button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onPrev} disabled={isFirst} data-testid={`button-${testIdPrefix}-back`}>
              Back
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onNext}
              className={actionButtonClass}
              data-testid={`button-${testIdPrefix}-next`}
            >
              {isLast ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    return url;
  } catch {
    return url;
  }
}
